/**
 * Hooks for the AI Data Explorer chat + saved "custom tables".
 */
import { useCallback, useMemo, useRef, useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useAuthStore } from '@/features/auth/stores/use-auth-store'
import { aiQuery, aiEdit, aiRun, fetchProjectSchema, type AiQueryResult, type AiFormSchema, type VegaLiteSpec } from './ai-explore-api'
import {
  createConversation, appendMessages,
  fetchSavedViews, softDeleteSavedView, setSavedViewVisibility, updateSavedViewColumns,
  listConversations, renameConversation, softDeleteConversation, getConversationMessages,
  createDraftView, updateDraftView, getView, finalizeView, touchConversation,
  listCharts, createChart, softDeleteChart,
} from './ai-storage'
import type { AiColumnConfig } from '../lib/ai-result-adapter'

export interface AiTurn {
  id: string
  role: 'user' | 'assistant'
  prompt?: string
  result?: AiQueryResult
  error?: string
}

/** The conversation's working draft — what the preview pane renders. */
export interface AiDraft {
  viewId: string
  title: string
  prompt: string
  sql: string
  dialect: string
  columns: string[]
  rows: Record<string, unknown>[]
  vega_lite: VegaLiteSpec | null
  truncated?: boolean
}

let _turnSeq = 0
const nextId = () => `t${++_turnSeq}`

const draftFromResult = (viewId: string, prompt: string, r: AiQueryResult): AiDraft => ({
  viewId,
  title: r.title ?? prompt.slice(0, 80),
  prompt,
  sql: r.sql ?? '',
  dialect: r.dialect ?? 'postgres',
  columns: r.columns ?? [],
  rows: r.rows ?? [],
  vega_lite: r.vega_lite ?? null,
  truncated: r.truncated,
})

/**
 * Conversational session: ONE working draft per conversation, refined in place across
 * edit turns. First turn = `aiQuery`; subsequent turns = `aiEdit` (sends the current
 * SQL + history). Persists conversation + messages; the draft is an is_draft saved
 * view updated in place; «finalize» promotes it.
 */
export function useAiChat(projectId: string | undefined) {
  const user = useAuthStore((s) => s.user)
  const qc = useQueryClient()
  const [turns, setTurns] = useState<AiTurn[]>([])
  const [draft, setDraft] = useState<AiDraft | null>(null)
  const [busy, setBusy] = useState(false)
  const [resuming, setResuming] = useState(false)

  const conversationId = useRef<string | null>(null)
  const draftViewId = useRef<string | null>(null)
  const turnsRef = useRef<AiTurn[]>([])
  const schemaCache = useRef<AiFormSchema[] | null>(null)
  turnsRef.current = turns

  const schema = useCallback(async () => {
    if (!schemaCache.current) schemaCache.current = await fetchProjectSchema(projectId!)
    return schemaCache.current
  }, [projectId])

  const historyPayload = () =>
    turnsRef.current.slice(-10).map((t) => ({
      role: t.role,
      content: t.role === 'user' ? (t.prompt ?? '') : (t.result?.title ?? ''),
    }))

  const send = useCallback(async (prompt: string) => {
    if (!projectId || !user || !prompt.trim() || busy) return
    setTurns((t) => [...t, { id: nextId(), role: 'user', prompt }])
    setBusy(true)
    const editing = !!draftViewId.current && !!draft?.sql
    try {
      const forms = await schema()
      const result = editing
        ? await aiEdit(projectId, prompt, draft!.sql, forms, historyPayload(), draft!.dialect)
        : await aiQuery(projectId, prompt, forms, historyPayload())

      setTurns((t) => [...t, { id: nextId(), role: 'assistant', result, error: result.ok ? undefined : result.message }])

      // Persist + update the draft in place (best-effort; non-fatal to the chat).
      try {
        if (!conversationId.current) {
          conversationId.current = await createConversation(projectId, user.id, result.title ?? prompt)
          qc.invalidateQueries({ queryKey: ['ai-conversations', projectId] })
        }
        await appendMessages(conversationId.current, prompt, result)

        if (result.ok && result.sql) {
          if (!draftViewId.current) {
            draftViewId.current = await createDraftView(projectId, user.id, conversationId.current, prompt, result)
          } else {
            await updateDraftView(draftViewId.current, {
              title: result.title, prompt, sql: result.sql, dialect: result.dialect,
              chart: result.vega_lite ?? null, columns: result.columns ?? null,
            })
          }
          setDraft(draftFromResult(draftViewId.current, prompt, result))
        }
        await touchConversation(conversationId.current).catch(() => {})
        qc.invalidateQueries({ queryKey: ['ai-conversations', projectId] })
      } catch { /* persistence is non-fatal */ }
      // On a failed edit we keep the previous draft untouched (don't clobber).
    } catch (e) {
      setTurns((t) => [...t, { id: nextId(), role: 'assistant', error: (e as Error).message }])
    } finally {
      setBusy(false)
    }
  }, [projectId, user, busy, draft, qc, schema])

  /** Open an existing conversation: load its thread + re-run its draft on fresh data. */
  const resume = useCallback(async (convId: string, draftViewIdToLoad: string | null) => {
    if (!projectId) return
    setResuming(true)
    conversationId.current = convId
    draftViewId.current = draftViewIdToLoad
    try {
      const msgs = await getConversationMessages(convId)
      setTurns(msgs.map((m) => ({
        id: nextId(),
        role: m.role,
        prompt: m.role === 'user' ? m.content : undefined,
        result: m.role === 'assistant' && m.sql
          ? { ok: !m.error, sql: m.sql, dialect: m.dialect ?? 'postgres', title: m.content, columns: m.columns ?? undefined, row_count: m.row_count ?? undefined, vega_lite: m.chart ?? undefined }
          : undefined,
        error: m.error ?? undefined,
      })))
      if (draftViewIdToLoad) {
        const view = await getView(draftViewIdToLoad)
        if (view?.sql) {
          const res = await aiRun(projectId, view.sql, view.dialect, 1000)
          setDraft({
            viewId: view.id, title: view.title, prompt: view.prompt ?? '',
            sql: view.sql, dialect: view.dialect,
            columns: res.columns ?? view.columns ?? [], rows: res.rows ?? [],
            vega_lite: view.chart ?? null, truncated: res.truncated,
          })
        }
      } else {
        setDraft(null)
      }
    } finally {
      setResuming(false)
    }
  }, [projectId])

  /** Promote the working draft to a reusable finalized view. */
  const finalize = useCallback(async (title?: string) => {
    if (!draftViewId.current) return
    await finalizeView(draftViewId.current, title)
    qc.invalidateQueries({ queryKey: ['ai-saved-views', projectId] })
    // the draft is now finalized; start a fresh draft on the next turn
    draftViewId.current = null
    setDraft(null)
  }, [projectId, qc])

  const reset = useCallback(() => {
    setTurns([]); setDraft(null)
    conversationId.current = null; draftViewId.current = null
    schemaCache.current = null
  }, [])

  return { turns, draft, busy, resuming, conversationId: conversationId.current, send, resume, finalize, reset }
}

export function useAiSavedViewsQuery(projectId: string | undefined) {
  return useQuery({
    queryKey: ['ai-saved-views', projectId],
    queryFn: () => fetchSavedViews(projectId!),
    enabled: !!projectId,
  })
}

export function useDeleteSavedViewMutation(projectId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => softDeleteSavedView(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['ai-saved-views', projectId] }),
  })
}

export function useSetSavedViewVisibilityMutation(projectId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, visibility }: { id: string; visibility: 'private' | 'project' }) =>
      setSavedViewVisibility(id, visibility),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['ai-saved-views', projectId] }),
  })
}

export function useUpdateSavedViewColumnsMutation(projectId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, config }: { id: string; config: AiColumnConfig }) => updateSavedViewColumns(id, config),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['ai-saved-views', projectId] }),
  })
}

/** Record-metadata aliases the LLM commonly selects → Persian labels. */
const META_LABELS: Record<string, string> = {
  display_id: 'شناسه رکورد', record_display_id: 'شناسه رکورد',
  record_status: 'وضعیت', status: 'وضعیت',
  record_number: 'شماره رکورد',
  created_at: 'تاریخ ایجاد', record_created_at: 'تاریخ ایجاد',
  updated_at: 'آخرین بروزرسانی', record_updated_at: 'آخرین بروزرسانی',
  submitted_at: 'تاریخ ثبت', approved_at: 'تاریخ تأیید',
  instance_index: 'شماره نمونه', soft_warning_count: 'هشدارها',
}

/**
 * Resolves a result-column alias to a friendly label using the project's form
 * field titles. Same alias in multiple forms with conflicting labels → keep the
 * raw alias (we can't know which form it came from). Cached per project.
 */
export function useProjectFieldLabels(projectId: string | undefined) {
  const q = useQuery({
    queryKey: ['ai-field-labels', projectId],
    queryFn: () => fetchProjectSchema(projectId!),
    enabled: !!projectId,
    staleTime: 5 * 60_000,
  })
  return useMemo(() => {
    const byKey = new Map<string, { label: string; ambiguous: boolean }>()
    for (const form of q.data ?? []) {
      for (const f of form.fields) {
        const ex = byKey.get(f.key)
        if (!ex) byKey.set(f.key, { label: f.label, ambiguous: false })
        else if (ex.label !== f.label) ex.ambiguous = true
      }
    }
    const labelFor = (col: string): string => {
      if (META_LABELS[col]) return META_LABELS[col]
      const hit = byKey.get(col)
      return hit && !hit.ambiguous ? hit.label : col
    }
    return labelFor
  }, [q.data])
}

// ─── Conversation history + charts ──────────────────────────────────────────

export function useConversationsQuery(projectId: string | undefined) {
  return useQuery({
    queryKey: ['ai-conversations', projectId],
    queryFn: () => listConversations(projectId!),
    enabled: !!projectId,
  })
}

export function useRenameConversationMutation(projectId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, title }: { id: string; title: string }) => renameConversation(id, title),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['ai-conversations', projectId] }),
  })
}

export function useDeleteConversationMutation(projectId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => softDeleteConversation(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['ai-conversations', projectId] }),
  })
}

export function useChartsQuery(projectId: string | undefined) {
  return useQuery({
    queryKey: ['ai-saved-charts', projectId],
    queryFn: () => listCharts(projectId!),
    enabled: !!projectId,
  })
}

export function useCreateChartMutation(projectId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: { ownerId: string; conversationId?: string | null; title: string; description?: string; vegaLite: VegaLiteSpec; sourceViewId?: string | null; sourceSql?: string | null; dialect?: string }) =>
      createChart(projectId, input.ownerId, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['ai-saved-charts', projectId] }),
  })
}

export function useDeleteChartMutation(projectId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => softDeleteChart(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['ai-saved-charts', projectId] }),
  })
}

/** Re-execute a saved view's SQL (fresh data + pagination). */
export function useRunSavedView(projectId: string | undefined) {
  return useCallback(
    (sql: string, dialect = 'postgres', limit?: number, offset?: number) =>
      aiRun(projectId!, sql, dialect, limit, offset),
    [projectId],
  )
}
